import React, { useState, useEffect } from 'react';

const OccupancyAndFeedback = () => {
  const [allGuestBookings, setAllGuestBookings] = useState([]);
  const [displayedGuestBookings, setDisplayedGuestBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define totalRooms as a constant or initial state if it's always 23
  // For demonstration, we'll hardcode it here based on your request.
  const TOTAL_ROOMS_CONSTANT = 23; // <-- Hardcoded total rooms

  const [roomSummary, setRoomSummary] = useState({
    totalRooms: 0, // This will be calculated from actual rooms data if available
    occupiedRooms: 0,
    availableRooms: 0,
    dirtyRooms: 0,
    serviceRooms: 0,
    cleanRooms: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3);

  useEffect(() => {
    const fetchGuestData = async () => {
      try {
        setLoading(true);
        setError(null);

        const bookingsResponse = await fetch('https://admin.thepridehotel.in/api/hotel_bookings');
        if (!bookingsResponse.ok) {
          throw new Error(`HTTP error! status: ${bookingsResponse.status}`);
        }
        const bookingsData = await bookingsResponse.json();
        console.log('Fetched Bookings:', bookingsData);

        const roomsResponse = await fetch('https://admin.thepridehotel.in/api/rooms');
        if (!roomsResponse.ok) {
          throw new Error(`HTTP error! status: ${roomsResponse.status}`);
        }
        const roomsData = await roomsResponse.json();
        console.log('Fetched Rooms:', roomsData);

        let occupied = 0;
        let available = 0;
        let dirty = 0;
        let service = 0;
        let clean = 0;

        roomsData.forEach(room => {
          if (room.status === 'Occupied') {
            occupied++;
          } else if (room.status === 'Available') {
            available++;
          }

          if (room.room_status && room.room_status.toLowerCase().includes('durty')) {
            dirty++;
          } else if (room.room_status && room.room_status.toLowerCase().includes('service')) {
            service++;
          } else if (room.room_status && room.room_status.toLowerCase().includes('clean')) {
            clean++;
          }
        });

        setRoomSummary({
          totalRooms: roomsData.length, // Still calculate actual total rooms if backend provides
          occupiedRooms: occupied,
          availableRooms: available,
          dirtyRooms: dirty,
          serviceRooms: service,
          cleanRooms: clean,
        });

        const assignedRoomNumbers = new Set();
        let intermediateGuestBookings = [];

        bookingsData
          .filter(booking => {
            const isRelevantStatus = booking.status === 'Reserved' || booking.status === 'Checked In';
            return isRelevantStatus;
          })
          .forEach(booking => {
            let roomNumber = 'N/A';
            let foundRoom = null;

            foundRoom = roomsData.find(room => room.booking_id === booking.id);

            if (foundRoom) {
              roomNumber = foundRoom.room_number;
              assignedRoomNumbers.add(roomNumber);
            } else {
              const availableRoomsOfType = roomsData.filter(room =>
                room.room_type === booking.room_type &&
                room.status === 'Occupied' &&
                !assignedRoomNumbers.has(room.room_number)
              );

              if (availableRoomsOfType.length > 0) {
                foundRoom = availableRoomsOfType[0];
                roomNumber = foundRoom.room_number;
                assignedRoomNumbers.add(roomNumber);
              }
            }

            intermediateGuestBookings.push({
              id: booking.id,
              name: booking.name,
              room_number: roomNumber,
              special_request: booking.special_request,
              submitted_at: booking.submitted_at,
            });
          });

        const sortedGuestBookings = intermediateGuestBookings.sort((a, b) => {
            return b.id - a.id;
        });

        setAllGuestBookings(sortedGuestBookings);

      } catch (e) {
        setError(e.message);
        console.error('Error fetching data:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchGuestData();
  }, []);

  useEffect(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = allGuestBookings.slice(indexOfFirstItem, indexOfLastItem);
    setDisplayedGuestBookings(currentItems);
  }, [allGuestBookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(allGuestBookings.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-button ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };

  const roomStatusChartData = [
    { label: 'Occupied', value: roomSummary.occupiedRooms, colorStart: '#d6a874', colorEnd: '#b07645' },
    { label: 'Available', value: roomSummary.availableRooms, colorStart: '#81C784', colorEnd: '#4CAF50' },
    { label: 'Dirty', value: roomSummary.dirtyRooms, colorStart: '#FFEB3B', colorEnd: '#FFC107' },
    { label: 'Service', value: roomSummary.serviceRooms, colorStart: '#EF9A9A', colorEnd: '#F44336' },
    { label: 'Clean', value: roomSummary.cleanRooms, colorStart: '#90CAF9', colorEnd: '#2196F3' },
  ];

  // Max value for scaling bars will be the TOTAL_ROOMS_CONSTANT
  // or the max of individual statuses if that's somehow higher.
  const maxChartValue = Math.max(TOTAL_ROOMS_CONSTANT, ...roomStatusChartData.map(d => d.value));

  return (
    <>
      <style>
        {`
        /* General Layout */
        .container {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 10px;
          padding-top: 0;
          background-color: #f3f4f6;
          font-family: Inter, sans-serif;
        }

        @media (min-width: 768px) {
          .container {
            flex-direction: row;
          }
        }

        /* Card Styling */
        .card {
          flex: 1;
          background-color: #fff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .heading {
          font-size: 17px;
          font-weight: 600;
          color: #111827;
        }

        .button {
          display: flex;
          align-items: center;
          padding: 6px 12px;
          border: 2px solid #2563eb;
          border-radius: 12px;
          font-size: 13px;
          background-color: #fff;
          cursor: pointer;
          transition: background-color 0.2s ease-in-out;
        }

        .button:hover {
          background-color: #eff6ff;
        }

        .button svg {
          margin-right: 4px;
        }

        .menu-icon {
          font-size: 18px;
          color: #9CA3AF;
          cursor: pointer;
        }

        /* Chart Specific Styles */
        .chart-container {
          position: relative;
          height: 180px;
          display: flex;
          align-items: flex-end;
          padding-left: 10px;
          padding-bottom: 5px;
          background-color: #fcfcfc;
          border-radius: 12px;
          margin-top: 15px;
          box-shadow: inset 0 0 5px rgba(0,0,0,0.03);
          overflow: visible; /* Changed to visible to ensure percentage text is not clipped */
        }

        .y-axis-labels {
          display: none;
        }

        .chart-bars {
          display: flex;
          flex: 1;
          justify-content: space-around;
          align-items: flex-end;
          height: 100%;
          padding: 0 10px;
        }

        .chart-bar-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 30px;
          height: 100%;
          justify-content: flex-end;
          margin: 0 8px;
          cursor: pointer;
        }

        .chart-bar {
          width: 100%;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          transition: all 0.3s ease-in-out;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }

        .chart-label {
          margin-top: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #4b5563;
          text-align: center;
        }

        /* Style for the percentage text */
        .bar-percentage {
            position: absolute;
            top: -25px; /* Adjust position above the bar */
            left: 50%;
            transform: translateX(-50%);
            background-color: #444; /* Darker background for contrast */
            color: white;
            padding: 3px 7px;
            border-radius: 5px;
            font-size: 11px; /* Slightly smaller for percentage */
            font-weight: 600;
            white-space: nowrap;
            opacity: 0;
            transition: opacity 0.2s ease-in-out, top 0.2s ease-in-out; /* Add transition for top */
            pointer-events: none;
            z-index: 10; /* Ensure it's above other elements */
            min-width: 45px; /* Ensure consistent width */
            text-align: center;
        }
        .chart-bar-container:hover .bar-percentage {
            opacity: 1;
            top: -30px; /* Slight lift on hover */
        }

        /* Guest Booking Item Styles */
        .guest-booking-item {
          position: relative;
          margin-top: 16px;
        }

        .guest-name {
          font-weight: 600;
          font-size: 14px;
          color: #111827;
        }

        .special-request {
          font-size: 14px;
          color: #4b5563;
          margin-top: 2px;
        }

        .guest-room-number {
          position: absolute;
          top: 0;
          right: 0;
          color: #4b5563;
          font-weight: 600;
          font-size: 14px;
        }

        .divider {
          border-top: 1px solid #d1d5db;
          margin-top: 12px;
        }

        /* Pagination Specific Styles */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 20px;
          gap: 8px;
        }

        .pagination-button {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background-color: #fff;
          cursor: pointer;
          font-size: 14px;
          color: #4b5563;
          transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
        }

        .pagination-button:hover:not(.active) {
          background-color: #eff6ff;
          border-color: #2563eb;
        }

        .pagination-button.active {
          background-color: #2563eb;
          color: #fff;
          border-color: #2563eb;
          font-weight: 600;
        }

        .pagination-button:disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background-color: #f3f4f6;
          color: #9CA3AF;
          border-color: #d1d5db;
        }
        `}
      </style>

      {/* Main container */}
      <div className="container">
        {/* Room Status Overview Card */}
        <div className="card">
          <div className="card-header">
            <h3 className="heading">Room Status Overview</h3>
            <button className="button">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Today
            </button>
          </div>

          {/* Chart Container */}
          <div className="chart-container">
            {/* Y-axis labels removed */}
            <div className="chart-bars">
              {roomStatusChartData.map((bar, index) => {
                const percentage = TOTAL_ROOMS_CONSTANT > 0
                  ? ((bar.value / TOTAL_ROOMS_CONSTANT) * 100).toFixed(1) // Calculate percentage
                  : 0;

                return (
                  <div
                    key={index}
                    className="chart-bar-container"
                    // No need for onMouseEnter/Leave state anymore, just direct hover CSS
                  >
                    <div className="bar-percentage">{percentage}%</div> {/* Display percentage */}
                    <div
                      className="chart-bar"
                      style={{
                        height: `${(bar.value / maxChartValue) * 100}%`,
                        background: `linear-gradient(to top, ${bar.colorStart}, ${bar.colorEnd})`
                      }}
                    ></div>
                    <div className="chart-label">{bar.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Guest Bookings Card */}
        <div className="card">
          <div className="card-header">
            <h3 className="heading">Guest Bookings</h3>
            <span className="menu-icon">⋮</span>
          </div>
          {loading && <p>Loading guest bookings...</p>}
          {error && <p className="error-message">Error: {error}</p>}
          {!loading && !error && (
            <>
              {displayedGuestBookings.length > 0 ? (
                displayedGuestBookings.map((guest, i) => (
                  <div key={guest.id || i} className="guest-booking-item">
                    <div>
                      <div className="guest-name">{guest.name}</div>
                      <div className="special-request">
                        {guest.special_request && guest.special_request.toLowerCase() !== 'na'
                          ? `Special Request: ${guest.special_request}`
                          : 'No special request'}
                      </div>
                    </div>
                    <div className="guest-room-number">Room: {guest.room_number}</div>
                    {i !== displayedGuestBookings.length - 1 && <hr className="divider" />}
                  </div>
                ))
              ) : (
                <p>No guest bookings to display matching current criteria (Reserved/Checked In status).</p>
              )}

              {allGuestBookings.length > itemsPerPage && (
                <div className="pagination-container">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>
                  {renderPageNumbers()}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OccupancyAndFeedback;